import { useLoginInfo } from '@/components/LoginInfo/provider';
import { useCallback, useState } from 'react';

const DEFAULT_DRAWER_WIDTH_STORAGE_KEY = 'invoiceReportDetailDrawerWidth:v1';

/**
 * Custom hook to manage drawer width with persistence in localStorage
 * @param defaultWidth - The default width to use if no saved width exists
 * @param storageKey - localStorage namespace; pass a feature-specific value to keep
 *   separate drawers (e.g. invoice_reports vs admin_invoice_reports) from sharing state.
 * @returns Object containing width state and setter function
 */
export const useDrawerWidth = (
  defaultWidth: number,
  storageKey: string = DEFAULT_DRAWER_WIDTH_STORAGE_KEY
) => {
  const { currentUser } = useLoginInfo();
  const userId = currentUser?.id;

  // Initialize width from localStorage or use default
  const [width, setWidthState] = useState<number>(() => {
    if (!userId) return defaultWidth;

    const fullKey = `${storageKey}_${userId}`;
    const savedWidth = window.localStorage.getItem(fullKey);

    if (savedWidth) {
      const parsedWidth = parseInt(savedWidth, 10);
      // Validate that the saved width is a valid number and >= defaultWidth
      if (!isNaN(parsedWidth) && parsedWidth >= defaultWidth) {
        return parsedWidth;
      }
    }

    return defaultWidth;
  });

  // Wrapper function to save width to localStorage when it changes
  const setWidth = useCallback(
    (newWidth: number) => {
      // Validate the new width - must be >= defaultWidth (minWidth)
      if (isNaN(newWidth) || newWidth < defaultWidth) {
        return;
      }

      setWidthState(newWidth);

      // Save to localStorage
      const fullKey = `${storageKey}_${userId}`;
      window.localStorage.setItem(fullKey, newWidth.toString());
    },
    [userId, defaultWidth, storageKey]
  );

  return { width, setWidth };
};
